import useScrollbar from "hooks/useScrollbar"; // Custom hook to manage scroll bar visibility
import useKeydown from "hooks/useKeydown"; // Custom hook to handle event listeners

/**
 * Custom hook to manage the state and behavior of a modal component.
 *
 * @returns {Object} An object containing:
 *   - `open` {boolean}: Indicates whether the modal is open or closed.
 *   - `handleOpen` {Function}: Function to open the modal.
 *   - `handleClose` {Function}: Function to close the modal, with an event parameter to stop propagation.
 *
 * @description
 * This hook provides functionality to open and close a modal, as well as manage
 * the side effects of opening/closing the modal, such as disabling body scroll
 * and handling the Escape key press to close the modal.
 *
 * Side Effects:
 * - When the modal is open, the body's scroll is disabled, and its position is fixed.
 * - When the modal is closed, the body's scroll and position are restored.
 *
 * Event Listeners:
 * - Adds a `keydown` event listener to close the modal when the Escape key is pressed.
 * - Cleans up the event listener when the component using this hook is unmounted.
 */
const useModal = () => {
  const [open, setOpen] = useScrollbar();

  const handleOpen = () => setOpen(true);

  const handleClose = (e) => {
    if (e) e.stopPropagation();
    setOpen(false);
  };

  // Custom hook to add and clean up the keydown event listener
  useKeydown(
    (e) => {
      if (e.key === "Escape" || e.key === "Esc") {
        e.preventDefault();
        setOpen(false);
      }
    },
    [open]
  );

  return [open, { handleOpen, handleClose, setOpen }];
};

export default useModal;
